import { Map as MapComponent, MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { useLatestDetections } from "@/hooks/use-latest-detections";
import { useSensorsQueryV1 } from "@/api/v1/sensor-api";
import { SensorsLayer } from "./map/sensor-layer";
import { DetectionLayer } from "./map/detections-layer";
import { GeofenceLayer } from "./map/geofence-layer";
import MovementEvents from "./map/movement-events";
import { useStore } from "@/store/store";
import { useCallback, useEffect, useRef } from "react";
import { AircraftLayer } from "./map/aircraft-layer";
import { ZoneDrawer } from "./map/zone-drawer";
import { MapControls } from "./map/map-controls";
import { NatsLayer } from "./map/nats-layer";
import { useSearch } from "@tanstack/react-router";
import { useFlightRestrictionZonesQueryV1 } from "@/api/v1/flight-restriction-zone-api";
import { calculateBounds } from "@/lib/geo-utils";

export const Map = () => {
  const latestDetections = useLatestDetections();
  const { data: sensors } = useSensorsQueryV1();
  const mapRef = useRef<MapRef>(null);
  
  // Use useSearch to get query parameters safely
  // We cast to any because the strict route typing might not know about 'zone' query param yet globally
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const searchParams = useSearch({ from: '/map' as any }) as any;
  const zoneDesignator = searchParams?.zone;

  const mapBounds = useStore(useCallback((state) => state.mapBounds, []));
  const mapZoom = useStore(useCallback((state) => state.mapZoom, []));
  const mapStyleUrl = useStore(useCallback((state) => state.mapStyleUrl, []));
  const mapProjection = useStore(useCallback((state) => state.mapProjection, []));
  
  const setSelectedZone = useStore((state) => state.setSelectedZone);
  const setZoneDrawerOpen = useStore((state) => state.setZoneDrawerOpen);
  const { data: zones } = useFlightRestrictionZonesQueryV1();

  // Handle zone selection from URL
  useEffect(() => {
    if (zoneDesignator && zones && mapRef.current) {
      const zone = zones.find(z => z.designator === zoneDesignator);
      if (zone) {
        setSelectedZone(zone);
        setZoneDrawerOpen(true);
        
        if (zone.polygon) {
          // Cast to correct type for utility
          const bounds = calculateBounds(zone.polygon as any);
          if (bounds) {
            mapRef.current.fitBounds(
              [
                [bounds[0], bounds[1]], // [minLng, minLat]
                [bounds[2], bounds[3]]  // [maxLng, maxLat]
              ],
              {
                padding: 50,
                duration: 2000
              }
            );
          }
        }
      }
    }
  }, [zoneDesignator, zones, setSelectedZone, setZoneDrawerOpen]);

  return (
    <div className="relative w-full h-full">
      <div className="absolute inset-0 z-0">
        <MapComponent
          ref={mapRef}
          id="map"
          initialViewState={{
            longitude: mapBounds[0],
            latitude: mapBounds[1],
            zoom: mapZoom,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle={mapStyleUrl}
          maxZoom={15}
          minZoom={0}
          maxPitch={0}
          interactive
          projection={mapProjection}
          interactiveLayerIds={["nats-fill", "nats-outline", "nats-labels"]}
        >
         
          <DetectionLayer detections={latestDetections} />
          <SensorsLayer sensors={sensors ?? []} />
          <GeofenceLayer />
          <NatsLayer />
          <AircraftLayer center={[mapBounds[0], mapBounds[1]]} zoom={mapZoom} radius={150} />
          <MovementEvents />

          {/* Map Controls - Airspace Summary and Zone Filter (inside map for context access) */}
          <MapControls />
        </MapComponent>
      </div>
      
      <ZoneDrawer />
    </div>
  );
};
